import { t } from 'i18next';
import {
  LitElement,
  PropertyValueMap,
  css,
  html,
  nothing,
  unsafeCSS,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import TomSelect from 'tom-select';

import { PrintOptionMethodDto } from '@/api-sdk';
import tomSelectMinified from '@/assets/css/tom-select.min.css?inline';
import {
  baseStyles,
  rootVariablesStyles,
  tomSelectStyles,
  typographyStyles,
} from '@/styles';
import { SelectOption, SelectVersion } from '@/types';

import '../../../../form/fg-select';

@customElement('fg-print-option-select')
export class FgPrintOptionSelect extends LitElement {
  @property({ type: Array })
  methods?: PrintOptionMethodDto[];

  @property({ type: Boolean })
  disabled = false;

  @property({ type: String })
  name = '';

  @state()
  tomSelect?: TomSelect;

  @state()
  noPrint = true;

  @state()
  chosenMethod?: PrintOptionMethodDto;

  @state()
  mappedOptions?: () => SelectOption[];

  @state()
  selectedValue: string | number = '';

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (_changedProperties.has('methods')) {
      this.methods?.map(method => {
        if (method.selected) {
          this.noPrint = false;
          this.chosenMethod = method;
        }
      });
    }

    if (!this.methods) {
      return;
    }

    const methods = this.methods.map((method, index) => ({
      value: index + 1,
      text: method.name,
      'sub-text': method.description,
      hsp: method.hspAvailable,
      selected: method.selected,
    }));

    this.mappedOptions = (): SelectOption[] => [
      {
        value: '',
        text: t('labels.noPrint'),
        'sub-text': '',
        hsp: false,
        selected: this.noPrint,
      },
      ...methods,
    ];

    this.selectedValue = this.methods.findIndex(method => method.selected) + 1;
  }

  /**
   * Handle the change event from the TomSelect library. Here we will check what print method is slecected and dispatch
   * the event to the configurator component to update the print option.
   *
   * @param self {FgPrintOptionSelect} - To prevent the this context from the TomSelect library, and used the LitElement context
   * @param event {string}
   */
  onPrintOptionChange(event: CustomEvent<{ value: string }>) {
    if (!this.methods) {
      return;
    }
    const value = event.detail.value;

    if (value === '') {
      this.noPrint = true;
      this.dispatchEvent(new CustomEvent('no-print-selected'));
      this.chosenMethod = undefined;
      return;
    }

    this.noPrint = false;

    const correctIndex = parseInt(value) - 1;
    const { queryString, name, description } = this.methods[correctIndex];

    this.onOptionSelected(queryString, name, description);
  }

  /**
   * Dispatch event to configurator component to update the print option. The configurator will handle the call to the API.
   * @param queryString {string}
   */
  onOptionSelected(
    queryString: string,
    methodName?: string,
    description?: string,
  ) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString: queryString,
          },
          method: methodName,
          description: description,
        },
      }),
    );
  }

  render() {
    if (this.mappedOptions === undefined) {
      return nothing;
    }

    return html`
      <fg-select
        ?disabled=${this.disabled}
        largeDropdown
        name=${this.name}
        .version=${SelectVersion.SINGLE_SELECT}
        .options=${this.mappedOptions()}
        .defaultValue=${this.selectedValue !== 0
          ? this.selectedValue.toString()
          : ''}
        @options-selected=${this.onPrintOptionChange}
      ></fg-select>
      ${this.chosenMethod
        ? html`
            <span class="chosen-method">${this.chosenMethod.description}</span>
          `
        : null}
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    typographyStyles,
    unsafeCSS(tomSelectMinified),
    tomSelectStyles,
    css`
      :host {
        height: 100%;
        width: 100%;
        display: grid;
        gap: 2px;
      }

      .chosen-method {
        color: var(--fg-gray--700, var(--fg-fallback-gray--700));
        font-size: 0.875rem;
        line-height: 1.375rem;
        font-weight: 400;
      }
    `,
  ];
}
